import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Checkbox } from 'primeng/checkbox';
import { ButtonDirective } from 'primeng/button';
import { Popover } from 'primeng/popover';
import { Textarea } from 'primeng/textarea';
import { ContentItemsFormComponent } from '../content-items-form/content-items-form';
import { SectionCardComponent } from '../section-card/section-card';
import { MediaPickerComponent } from '../media-picker/media-picker';
import { AttributesEditorComponent } from '../attributes-editor/attributes-editor';
import { VarPickerComponent } from '../var-picker/var-picker';
import { VariantEditorComponent, type VariantData } from '../variant-editor/variant-editor';
import { EditorFilesService, type JobStatusResponse } from '../../../features/editor/services/editor-files.service';
import { EditorJobsService } from '../../../features/editor/services/editor-jobs.service';
import { EditorStateService } from '../../../features/editor/services/editor-state.service';
import { ChapterData, FileCategory, JsonData, SceneAction, SceneData } from '../../models';
import { deriveSlug, generateEntityId, makeUniqueSlug } from '../../utils/slug';
import { slugOptions } from '../../utils/slug-options';
import { cloneDeep } from '../../utils/clone';
import { humanizeEnum } from '../../utils/format';
import { parseAeKeyframes, parseAeCornerPin, cornerPinToButtonKeyframes, actionToAeCornerPin, AeComp } from '../../utils/ae-converter';
import { shouldStampVideoNp, mediaHasGeneratedVersions } from '../../utils/media-readiness';

@Component({
	selector: 'app-scene-form',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, Select, InputText, InputNumber, Checkbox, ButtonDirective, Popover, Textarea, ContentItemsFormComponent, SectionCardComponent, MediaPickerComponent, AttributesEditorComponent, VariantEditorComponent, VarPickerComponent],
	templateUrl: './scene-form.html',
	styleUrl: './scene-form.scss',
})
export class SceneFormComponent {
	 
	data = input.required<any>();
	dataChange = output<JsonData>();
	slugRenamed = output<{ oldSlug: string; newSlug: string }>();
	requestSave = output<void>();
	copyItem = output<JsonData>();
	copyAction = output<SceneAction>();
	projectFps = input<number>(25);
	aeComp = input<AeComp>({ fps: 25, width: 1920, height: 1080 });

	/**
	 * PRO-210: the scene's own `attributes`/`variant` — these live on the
	 * `SceneRef` inside project.json (not on this scene's own `scene.json`),
	 * so they're passed in/out rather than read from `data()`. The editor
	 * resolves the ref and persists changes to project.json.
	 */
	sceneRefAttributes = input<Record<string, unknown> | null>(null);
	sceneRefAttributesChange = output<Record<string, unknown>>();
	sceneRefVariant = input<VariantData | null>(null);
	sceneRefVariantChange = output<VariantData | null>();
	sceneRefAlt = input(false);
	sceneRefAltChange = output<boolean>();
	argKeys = input<string[]>([]);
	siblingSlug = input<string[]>([]);
	chapters = input<ChapterData[]>([]);

	protected pickerVisible = signal(false);
	protected pickerCategories = signal<FileCategory[]>([]);
	protected maskPickerVisible = signal(false);
	protected readonly maskPickerCategories: FileCategory[] = ['mask'];

	private filesService = inject(EditorFilesService);
	private jobsService = inject(EditorJobsService);
	private stateService = inject(EditorStateService);
	private destroyRef = inject(DestroyRef);

	/** The fetch path for this scene — set by the editor when selecting a scene. */
	scenePath = input<string>('');

	/**
	 * DEV-128: mirrors a composite auto-fired by `EditorJobsService` (not this
	 * component's own button) into `compositeMediaId`, so the "Queued"
	 * indicator under the button shows up regardless of who triggered it.
	 */
	private readonly syncAutoComposite = effect(() => {
		const composite = this.stateService.lastVideoMaskComposite();
		if (!composite) return;
		const source = this.data()?.background?.source;
		if (composite.videoBaseUrl !== source?.src || composite.maskBaseUrl !== source?.maskSrc) return;
		this.compositeMediaId.set(composite.mediaId);
	});

	// ── Version generation state (from central jobs service) ────────────────
	protected generateStatus = computed<JobStatusResponse | null>(() => {
		const mediaId = this.currentBgMedia()?.id;
		if (!mediaId) return null;
		const job = this.jobsService.getJobForMedia(mediaId);
		if (!job) return null;
		return {
			jobId: job.jobId,
			type: job.type,
			mediaId: job.mediaId,
			status: job.status,
			progress: job.progress,
			currentStep: job.currentStep,
			completedVersions: job.completedVersions,
			totalVersions: job.totalVersions,
			error: job.error,
		};
	});

	/** The currently referenced background media, looked up in the project's file pool by baseUrl. */
	protected currentBgMedia = computed(() => {
		const src = this.data()?.background?.source?.src;
		if (!src || typeof src !== 'string') return undefined;
		return this.filesService.findByBaseUrl(src);
	});

	/** True when the current bg src is a local media (only local media can be generated). */
	protected canGenerate = computed(() => !!this.currentBgMedia()?.isLocal);

	/** True when versions beyond 'original' have been generated. */
	protected hasVersions = computed(() => (this.currentBgMedia()?.versionCount ?? 0) > 1);

	protected readonly bgTypeOptions = ['VIDEO', 'IMAGE']
		.map(value => ({ label: humanizeEnum(value), value }));
	protected readonly srcTypeOptions = ['RELATIVE', 'ABSOLUTE']
		.map(value => ({ label: humanizeEnum(value), value }));
	protected readonly playbackTypeOptions = [
		{ label: '(none)', value: '' },
		...['VIDEO', 'VIDEO_NP'].map(value => ({ label: humanizeEnum(value), value })),
	];

	 
	protected patch(patcher: (draft: any) => void): void {
		const clone = cloneDeep(this.data() ?? {});
		patcher(clone);
		this.dataChange.emit(clone);
		// Sync to shared state service
		const path = this.scenePath();
		if (path) this.stateService.setScene(path, clone as SceneData);
	}

	protected fileName(url: string | undefined): string {
		if (!url) return '';
		// External CDN — show full URL
		if (url.startsWith('https://')) return url;
		// Local — show original filename from media pool, or extract from URL
		const media = this.filesService.findByBaseUrl(url);
		return media?.name ?? url.split('/').pop() ?? url;
	}

	protected onSlugChange(newSlug: string): void {
		const oldSlug = this.data()?.slug;
		if (!newSlug?.trim() || newSlug === oldSlug) return;
		this.slugRenamed.emit({ oldSlug, newSlug: newSlug.trim() });
	}

	protected setField(field: keyof SceneData, value: unknown): void {
		this.patch(d => { d[field] = value; });
	}

	protected setBool(field: keyof SceneData, value: boolean): void {
		this.patch(d => {
			d[field] = value;
		});
	}

	protected setFramesCount(val: number | null): void {
		this.patch(d => d.framesCount = val ?? 0);
	}

	/** Duration in seconds, derived from framesCount/fps — used for IMAGE-background scenes, whose length has no probed source and must be set by hand (PRO-196). */
	protected durationSeconds = computed(() => {
		const fps = this.projectFps();
		if (!fps) return 0;
		return Math.round(((this.data()?.framesCount ?? 0) / fps) * 100) / 100;
	});

	protected setDurationSeconds(val: number | null): void {
		const fps = this.projectFps();
		this.patch(d => d.framesCount = Math.round((val ?? 0) * fps));
	}

	protected setBgSrc(value: string): void {
		const media = this.filesService.findByBaseUrl(value);
		this.patch(d => {
			if (!d.background) d.background = {};
			if (!d.background.source) d.background.source = {};
			d.background.source.src = value;
			d.background.source.srcType = 'ABSOLUTE';
			if (!d.background.source.dataType) d.background.source.dataType = 'VIDEO';

			// playbackType/auto-computed framesCount only make sense for an
			// actual video source — a static IMAGE background has no probed
			// duration and its length is set by hand via the Duration field.
			if (d.background.source.dataType === 'VIDEO') {
				// Only while generation is still to come: nothing but the job's
				// completion event clears this flag, so stamping an already
				// generated media renders the scene black for good (DEV-530).
				if (shouldStampVideoNp(media)) d.playbackType = 'VIDEO_NP';
				else delete (d as { playbackType?: unknown }).playbackType;
				if (media?.durationSeconds && media.durationSeconds > 0) {
					const fps = media.fps || this.projectFps();
					d.framesCount = Math.round(media.durationSeconds * fps);
				}
			}
		});

		// Auto-trigger version generation if local and not yet generated
		if (media?.isLocal && media.id && !mediaHasGeneratedVersions(media)) {
			this.autoGenerate(media.id, media.name ?? '');
		} else if (media?.isLocal && media.id && mediaHasGeneratedVersions(media)) {
			// Video is already generated — if the scene's mask is too, combine right
			// away (no version-gen job will run, so there's no completion event to
			// hook the auto-composite off otherwise).
			const maskSrc = this.data()?.background?.source?.maskSrc;
			const maskMedia = maskSrc ? this.filesService.findByBaseUrl(maskSrc) : undefined;
			if (maskMedia?.id && maskMedia.isLocal && (maskMedia.versionCount ?? 0) > 1) {
				this.fireComposite(media.id, maskMedia.id);
			}
		}
	}

	protected clearBgSrc(): void {
		this.patch(d => {
			// Remove the WHOLE background, not just `src`. Deleting `src` alone left
			// `{ srcType:'ABSOLUTE', dataType:'VIDEO' }` behind — a source the player's
			// prepareScene dereferences unguarded (`generateAbsFilePath(undefined)
			// .replace(...)`), which throws and aborts init for the ENTIRE player, not
			// just this scene. A background with no media isn't a background.
			delete d.background;
			// playbackType (VIDEO_NP) only means something alongside a video background.
			delete d.playbackType;
		});
	}

	protected setBgField(field: string, value: string | boolean): void {
		this.patch(d => {
			if (!d.background) d.background = {};
			if (!d.background.source) d.background.source = {};
			d.background.source[field] = value;
			// A stale VIDEO_NP/VIDEO marker from a previous video background is
			// meaningless once the type is switched to a static image.
			if (field === 'dataType' && value === 'IMAGE') delete d.playbackType;
		});
	}

	protected setMask(value: boolean): void {
		this.patch(d => {
			if (value) d.mask = true;
			else delete d.mask;
		});
	}

	protected setMaskSrc(value: string): void {
		this.patch(d => {
			if (!d.background) d.background = {};
			if (!d.background.source) d.background.source = {};
			d.background.source.maskSrc = value;
		});
		const media = this.filesService.findByBaseUrl(value);
		if (media?.isLocal && media.id && !mediaHasGeneratedVersions(media)) {
			this.autoGenerate(media.id, media.name ?? '');
		} else if (media?.isLocal && media.id && mediaHasGeneratedVersions(media)) {
			// Mask is already generated — if the scene's video is too, combine
			// right away (no version-gen job will run, so there's no completion
			// event to hook the auto-composite off otherwise).
			const bgSrc = this.data()?.background?.source?.src;
			const bgMedia = bgSrc ? this.filesService.findByBaseUrl(bgSrc) : undefined;
			if (bgMedia?.id && bgMedia.isLocal && (bgMedia.versionCount ?? 0) > 1) {
				this.fireComposite(bgMedia.id, media.id);
			}
		}
	}

	protected openMaskPicker(): void {
		this.maskPickerVisible.set(true);
	}

	protected openBgPicker(): void {
		const bgType = this.data()?.background?.source?.dataType ?? 'VIDEO';
		const hasMask = this.data()?.mask;
		if (bgType === 'VIDEO') {
			this.pickerCategories.set(hasMask ? ['video', 'video-mask'] : ['video']);
		} else {
			this.pickerCategories.set(['images']);
		}
		this.pickerVisible.set(true);
	}

	protected onContentChange(content: JsonData[]): void {
		this.patch(d => {
			d.content = content;
		});
	}

	// ── Actions ──────────────────────────────────────────────────────────────
	protected readonly actionTypeOptions = [
		'SEEK', 'LINK', 'SHARE', 'BLANK', 'DIALOG', 'PLAY', 'TOGGLE', 'COPY_TEXT',
	].map(value => ({ label: humanizeEnum(value), value }));
	protected newActionType = signal('LINK');
	protected actionExpanded = signal<Set<number>>(new Set());

	/** Empty value = stay in the scene's own chapter (SEEK omits `data.chapter` then). */
	protected chapterOptions(current?: string | null): { label: string; value: string }[] {
		return [
			{ label: '(current)', value: '' },
			...slugOptions(this.chapters().map(ch => ch.slug), current),
		];
	}

	protected sceneSlugOptions(current?: string | null): { label: string; value: string }[] {
		return slugOptions(this.siblingSlug(), current);
	}

	protected sceneOptionsFor(chapterSlug: string | undefined, current?: string | null): { label: string; value: string }[] {
		if (!chapterSlug) return this.sceneSlugOptions(current);
		const scenes = this.chapters().find(ch => ch.slug === chapterSlug)?.scenes ?? [];
		return slugOptions(scenes.map(s => s.slug), current);
	}

	protected setSeekChapter(index: number, chapterSlug: string): void {
		const validScenes = new Set(this.sceneOptionsFor(chapterSlug || undefined).map(o => o.value));
		this.patch(d => {
			const action = d.actions?.[index];
			if (!action) return;
			if (!action.data) action.data = {};
			if (chapterSlug) action.data.chapter = chapterSlug;
			else delete action.data.chapter;
			if (action.data.scene && !validScenes.has(action.data.scene)) delete action.data.scene;
		});
	}

	protected toggleAction(index: number): void {
		this.actionExpanded.update(set => {
			const next = new Set(set);
			if (next.has(index)) next.delete(index); else next.add(index);
			return next;
		});
	}

	protected addAction(): void {
		const type = this.newActionType();
		this.patch(d => {
			if (!d.actions) d.actions = [];
			const taken = new Set<string>(d.actions.map((a: { slug?: string }) => a.slug).filter((s: string | undefined): s is string => !!s));
			const base = (type as string).toLowerCase() || 'action';
			const slug = makeUniqueSlug(deriveSlug(base), taken);
			d.actions.push({ id: generateEntityId(), name: '', slug, type, coords: { x: 0, y: 0 }, size: { width: 100, height: 50 } });
		});
	}

	protected requestCopyAction(index: number): void {
		const action = this.data()?.actions?.[index];
		if (action) this.copyAction.emit(action as SceneAction);
	}

	protected removeAction(index: number): void {
		if (!confirm('Delete this action?')) return;
		this.patch(d => {
			if (d.actions) d.actions.splice(index, 1);
		});
		this.actionExpanded.update(set => {
			const next = new Set<number>();
			for (const i of set) {
				if (i < index) next.add(i);
				else if (i > index) next.add(i - 1);
			}
			return next;
		});
	}

	protected moveAction(index: number, dir: -1 | 1): void {
		this.patch(d => {
			const arr = d.actions;
			if (!Array.isArray(arr)) return;
			const j = index + dir;
			if (j < 0 || j >= arr.length) return;
			[arr[index], arr[j]] = [arr[j], arr[index]];
		});
		const j = index + dir;
		this.actionExpanded.update(set => {
			const hadIndex = set.has(index);
			const hadJ = set.has(j);
			if (hadIndex === hadJ) return set;
			const next = new Set(set);
			if (hadIndex) { next.delete(index); next.add(j); }
			else { next.delete(j); next.add(index); }
			return next;
		});
	}

	protected setActionField(index: number, field: string, value: unknown): void {
		this.patch(d => {
			if (d.actions?.[index]) d.actions[index][field] = value;
		});
	}

	protected setActionCoord(index: number, axis: 'x' | 'y', value: number | null): void {
		this.patch(d => {
			const action = d.actions?.[index];
			if (!action) return;
			if (!action.coords) action.coords = { x: 0, y: 0 };
			action.coords[axis] = value ?? 0;
		});
	}

	protected setActionSize(index: number, axis: 'width' | 'height', value: number | null): void {
		this.patch(d => {
			const action = d.actions?.[index];
			if (!action) return;
			if (!action.size) action.size = { width: 0, height: 0 };
			action.size[axis] = value ?? 0;
		});
	}

	protected setActionDataField(index: number, field: string, value: unknown): void {
		this.patch(d => {
			const action = d.actions?.[index];
			if (!action) return;
			if (!action.data) action.data = {};
			if (value === '' || value === null || value === undefined) delete action.data[field];
			else action.data[field] = value;
		});
	}

	protected getActionMode(action: { coords?: unknown; frames?: Record<string, unknown> }): 'static' | 'dynamic' | null {
		if (action.frames && Object.keys(action.frames).length > 0) return 'dynamic';
		if (action.coords) return 'static';
		return null;
	}

	protected getActionCoordsSummary(action: { coords?: { x: number; y: number }; frames?: Record<string, { coords: { x: number; y: number } }> }): string {
		if (action.frames && Object.keys(action.frames).length > 0) {
			const entries = Object.entries(action.frames).sort((a, b) => Number(a[0]) - Number(b[0]));
			const rows = entries.map(([f, v]) =>
				`<tr><td>${f}</td><td>${v.coords.x.toFixed(1)}</td><td>${v.coords.y.toFixed(1)}</td></tr>`
			).join('');
			return `<table class="ae-tip-table"><thead><tr><th>Frame</th><th>X</th><th>Y</th></tr></thead><tbody>${rows}</tbody></table>`;
		}
		if (action.coords) {
			return `<table class="ae-tip-table"><thead><tr><th>X</th><th>Y</th></tr></thead><tbody><tr><td>${action.coords.x.toFixed(1)}</td><td>${action.coords.y.toFixed(1)}</td></tr></tbody></table>`;
		}
		return '';
	}

	protected clearActionCoords(ai: number): void {
		this.patch(d => {
			const action = d.actions?.[ai];
			if (!action) return;
			delete action.coords;
			delete action.frames;
		});
	}

	protected setActionMode(ai: number, mode: 'static' | 'dynamic'): void {
		this.patch(d => {
			const action = d.actions?.[ai];
			if (!action) return;
			if (mode === 'static') {
				if (action.frames) {
					const entries = Object.values(action.frames) as Array<{ coords: { x: number; y: number } }>;
					if (entries.length > 0 && entries[0]?.coords) action.coords = { ...entries[0].coords };
					delete action.frames;
				}
			} else {
				if (!action.frames) {
					action.frames = action.coords ? { '0': { coords: { ...action.coords } } } : {};
				}
				delete action.coords;
			}
		});
	}

	protected aeInputOpenForAi = signal<number | null>(null);
	private coordsHideTimer: ReturnType<typeof setTimeout> | null = null;

	protected showCoordsPopover(pop: { show: (e: MouseEvent) => void }, event: MouseEvent): void {
		if (this.coordsHideTimer) { clearTimeout(this.coordsHideTimer); this.coordsHideTimer = null; }
		pop.show(event);
	}

	protected hideCoordsPopover(pop: { hide: () => void }): void {
		this.coordsHideTimer = setTimeout(() => pop.hide(), 200);
	}

	protected cancelCoordsHide(): void {
		if (this.coordsHideTimer) { clearTimeout(this.coordsHideTimer); this.coordsHideTimer = null; }
	}
	protected aeImportStatus = signal<{ ai: number; type: 'ok' | 'error'; message: string } | null>(null);
	private aeStatusTimer: ReturnType<typeof setTimeout> | null = null;

	private setAeStatus(ai: number, type: 'ok' | 'error', message: string): void {
		if (this.aeStatusTimer !== null) clearTimeout(this.aeStatusTimer);
		this.aeImportStatus.set({ ai, type, message });
		this.aeStatusTimer = setTimeout(() => this.aeImportStatus.set(null), 4000);
	}

	protected openAeInput(ai: number): void {
		this.aeInputOpenForAi.set(ai);
		setTimeout(() => {
			(document.querySelector(`textarea[data-ae-input="${ai}"]`) as HTMLTextAreaElement | null)?.focus();
		}, 0);
	}

	protected closeAeInput(): void {
		this.aeInputOpenForAi.set(null);
	}

	/**
	 * PRO-192: a full 4-corner-pin AE export (same format content-item-card
	 * already parses via `parseAeCornerPin`) lets us derive both the button's
	 * top-left position AND its size — mathematically, from corner-to-corner
	 * distance — instead of requiring the size to be typed in by hand.
	 * Falls back to the pre-existing single-point parser (`parseAeKeyframes`)
	 * for exports that only carry one corner/property, so old single-point
	 * pastes keep working unchanged.
	 *
	 * Size is written once (from the first available frame's corners) even in
	 * dynamic mode — `SceneAction.frames` has no per-frame size slot, and a
	 * button's size doesn't animate in practice, only its position/perspective
	 * does.
	 */
	private processAeImport(ai: number, text: string): boolean {
		if (!text.trim()) {
			this.setAeStatus(ai, 'error', 'No text to parse');
			return false;
		}

		const cornerPin = parseAeCornerPin(text);
		const hasCornerPinData = cornerPin.upperLeft.length > 0 && cornerPin.upperRight.length > 0 && cornerPin.lowerLeft.length > 0;
		if (hasCornerPinData) {
			return this.applyCornerPinImport(ai, cornerPin);
		}

		const { keyframes, skippedNegative } = parseAeKeyframes(text);
		if (keyframes.length === 0) {
			this.setAeStatus(ai, 'error', 'No valid keyframe data — check AE export format');
			return false;
		}
		this.patch(d => {
			const action = d.actions?.[ai];
			if (!action) return;
			if (keyframes.length === 1) {
				action.coords = { x: keyframes[0].x, y: keyframes[0].y };
				delete action.frames;
			} else {
				action.frames = {};
				for (const f of keyframes) {
					action.frames[String(f.frame)] = { coords: { x: f.x, y: f.y } };
				}
				delete action.coords;
			}
		});
		const kfLabel = keyframes.length === 1 ? '1 keyframe' : `${keyframes.length} keyframes`;
		const skipSuffix = skippedNegative > 0 ? ` (${skippedNegative} negative skipped)` : '';
		this.setAeStatus(ai, 'ok', `${kfLabel} imported${skipSuffix}`);
		return true;
	}

	private applyCornerPinImport(ai: number, cornerPin: ReturnType<typeof parseAeCornerPin>): boolean {
		const result = cornerPinToButtonKeyframes(cornerPin);
		if (!result) {
			this.setAeStatus(ai, 'error', 'Corner pin data incomplete — need Upper Left, Upper Right and Lower Left at a shared frame');
			return false;
		}
		const { rect, frames } = result;

		this.patch(d => {
			const action = d.actions?.[ai];
			if (!action) return;
			action.size = { width: Math.round(rect.width), height: Math.round(rect.height) };
			if (frames.length === 1) {
				action.coords = { x: rect.x, y: rect.y };
				delete action.frames;
			} else {
				action.frames = {};
				for (const f of frames) {
					action.frames[String(f.frame)] = { coords: { x: f.x, y: f.y } };
				}
				delete action.coords;
			}
		});

		const frameLabel = frames.length === 1 ? '1 frame' : `${frames.length} frames`;
		this.setAeStatus(ai, 'ok', `4 corners → position + size (${Math.round(rect.width)}×${Math.round(rect.height)}) imported, ${frameLabel}`);
		return true;
	}

	protected async exportAeToClipboard(ai: number): Promise<void> {
		const action = this.data()?.actions?.[ai];
		if (!action) return;
		try {
			await navigator.clipboard.writeText(actionToAeCornerPin(action, this.aeComp()));
			const frameCount = action.frames ? Object.keys(action.frames).length : 1;
			this.setAeStatus(ai, 'ok', `${frameCount} frame(s) copied — paste onto a Corner Pin effect in AE`);
		} catch {
			this.setAeStatus(ai, 'error', 'Could not write to clipboard');
		}
	}

	protected onAeTextareaPaste(ai: number, event: ClipboardEvent): void {
		const text = event.clipboardData?.getData('text/plain') ?? '';
		if (!text.trim()) return;
		event.preventDefault();
		const ok = this.processAeImport(ai, text);
		if (ok) this.aeInputOpenForAi.set(null);
	}

	protected onAeTextareaKeydown(ai: number, event: KeyboardEvent, el: HTMLTextAreaElement): void {
		if (event.key === 'Escape') {
			event.preventDefault();
			this.aeInputOpenForAi.set(null);
		} else if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
			event.preventDefault();
			const ok = this.processAeImport(ai, el.value);
			if (ok) this.aeInputOpenForAi.set(null);
		}
	}

	protected importAeFromTextarea(ai: number, text: string): void {
		const ok = this.processAeImport(ai, text);
		if (ok) this.aeInputOpenForAi.set(null);
	}

	protected actionSourcePickerVisible = signal(false);
	protected actionSourcePickerIndex = signal(-1);

	protected openActionSourcePicker(ai: number): void {
		this.actionSourcePickerIndex.set(ai);
		this.actionSourcePickerVisible.set(true);
	}

	protected clearActionSource(ai: number): void {
		this.patch(d => {
			const action = d.actions?.[ai];
			if (action?.source) delete action.source.src;
		});
	}

	protected setActionSourceSrc(src: string): void {
		const ai = this.actionSourcePickerIndex();
		if (ai < 0) return;
		this.patch(d => {
			const action = d.actions?.[ai];
			if (!action) return;
			if (!action.source) action.source = {};
			action.source.src = src;
			if (!action.source.srcType) action.source.srcType = 'ABSOLUTE';
			if (!action.source.dataType) action.source.dataType = 'IMAGE';
		});
	}

	protected setActionSourceField(ai: number, field: string, value: unknown): void {
		this.patch(d => {
			const action = d.actions?.[ai];
			if (!action) return;
			if (!action.source) action.source = {};
			(action.source as Record<string, unknown>)[field] = value;
		});
	}

	protected setActionAttributes(index: number, attrs: Record<string, unknown>): void {
		this.patch(d => {
			const action = d.actions?.[index];
			if (!action) return;
			const keys = Object.keys(attrs);
			if (keys.length > 0) {
				action.attributes = attrs;
				action.groups = keys;
			} else {
				delete action.attributes;
				delete action.groups;
			}
		});
	}

	// ── Effects ──────────────────────────────────────────────────────────────
	protected readonly effectTriggerOptions = [
		'BEFORE_SCENE_START', 'AFTER_SCENE_START', 'BEFORE_SCENE_END', 'AFTER_SCENE_END', 'BEFORE_SCENE_FRAME',
	].map(value => ({ label: humanizeEnum(value), value }));
	protected readonly effectMethodOptions = [
		'SET', 'EVALUATE', 'WAIT', 'SUBTRACT',
	].map(value => ({ label: humanizeEnum(value), value }));
	protected newEffectMethod = signal('SET');
	protected effectExpanded = signal<Set<number>>(new Set());

	protected toggleEffect(index: number): void {
		this.effectExpanded.update(set => {
			const next = new Set(set);
			if (next.has(index)) next.delete(index); else next.add(index);
			return next;
		});
	}

	protected addEffect(): void {
		const method = this.newEffectMethod();
		this.patch(d => {
			if (!d.effects) d.effects = [];
			d.effects.push({ trigger: 'BEFORE_SCENE_START', name: '', data: { method } });
		});
	}

	protected removeEffect(index: number): void {
		if (!confirm('Delete this effect?')) return;
		this.patch(d => {
			if (d.effects) d.effects.splice(index, 1);
		});
		this.effectExpanded.update(set => {
			const next = new Set<number>();
			for (const i of set) {
				if (i < index) next.add(i);
				else if (i > index) next.add(i - 1);
			}
			return next;
		});
	}

	protected moveEffect(index: number, dir: -1 | 1): void {
		this.patch(d => {
			const arr = d.effects;
			if (!Array.isArray(arr)) return;
			const j = index + dir;
			if (j < 0 || j >= arr.length) return;
			[arr[index], arr[j]] = [arr[j], arr[index]];
		});
		const j = index + dir;
		this.effectExpanded.update(set => {
			const hadIndex = set.has(index);
			const hadJ = set.has(j);
			if (hadIndex === hadJ) return set;
			const next = new Set(set);
			if (hadIndex) { next.delete(index); next.add(j); }
			else { next.delete(j); next.add(index); }
			return next;
		});
	}

	protected setEffectField(index: number, field: string, value: unknown): void {
		this.patch(d => {
			const effect = d.effects?.[index];
			if (!effect) return;
			if (value === '' || value === null || value === undefined) delete effect[field];
			else effect[field] = value;
			// The form only offers a frame number on the frame trigger, so a leftover
			// one would be unreachable state the user can't see or clear.
			if (field === 'trigger' && value !== 'BEFORE_SCENE_FRAME') delete effect.frameIndex;
		});
	}

	/** Which `data` keys each method owns — anything else is pruned on a method switch. */
	private static readonly EFFECT_METHOD_KEYS: Record<string, string[]> = {
		SET: ['input', 'attributes'],
		SUBTRACT: ['input', 'attributes'],
		WAIT: ['chapter', 'scene'],
		EVALUATE: ['input', 'output'],
	};

	/**
	 * Changing the method hides the previous method's fields, so leaving their
	 * values behind would ship data to the player that the editor can no longer
	 * show or clear. Unknown keys are left alone — they belong to hand-authored
	 * methods this form doesn't model.
	 */
	protected setEffectMethod(index: number, method: string): void {
		const keep = new Set(SceneFormComponent.EFFECT_METHOD_KEYS[method] ?? []);
		const managed = Object.values(SceneFormComponent.EFFECT_METHOD_KEYS).flat();
		this.patch(d => {
			const effect = d.effects?.[index];
			if (!effect) return;
			if (!effect.data) effect.data = {};
			effect.data.method = method;
			for (const key of managed) {
				if (!keep.has(key)) delete effect.data[key];
			}
		});
	}

	protected setEffectDataField(index: number, field: string, value: unknown): void {
		this.patch(d => {
			const effect = d.effects?.[index];
			if (!effect) return;
			if (!effect.data) effect.data = {};
			if (value === '' || value === null || value === undefined) delete effect.data[field];
			else effect.data[field] = value;
		});
	}

	/** Mirrors `setSeekChapter` — WAIT's target scene must stay valid for the newly picked chapter. */
	protected setEffectChapter(index: number, chapterSlug: string): void {
		const validScenes = new Set(this.sceneOptionsFor(chapterSlug || undefined).map(o => o.value));
		this.patch(d => {
			const effect = d.effects?.[index];
			if (!effect) return;
			if (!effect.data) effect.data = {};
			if (chapterSlug) effect.data.chapter = chapterSlug;
			else delete effect.data.chapter;
			if (effect.data.scene && !validScenes.has(effect.data.scene)) delete effect.data.scene;
		});
	}

	protected setEffectConditionField(index: number, field: 'min' | 'draw', value: number | null): void {
		this.patch(d => {
			const effect = d.effects?.[index];
			if (!effect) return;
			if (!effect.data) effect.data = {};
			if (!effect.data.output) effect.data.output = { condition: { min: 0 } };
			if (value === null && field === 'draw') delete effect.data.output.condition[field];
			else effect.data.output.condition[field] = value ?? 0;
		});
	}

	protected setEffectOutputAttributes(index: number, branch: 'success' | 'fail' | 'draw', attrs: Record<string, unknown>): void {
		this.patch(d => {
			const effect = d.effects?.[index];
			if (!effect) return;
			if (!effect.data) effect.data = {};
			if (!effect.data.output) effect.data.output = { condition: { min: 0 } };
			if (Object.keys(attrs).length > 0) {
				if (!effect.data.output[branch]) effect.data.output[branch] = {};
				effect.data.output[branch].attributes = attrs;
			} else if (effect.data.output[branch]) {
				// Drop the whole branch — an empty `draw: {}` could read to the player
				// as "a draw outcome exists", which is not what an empty editor means.
				// Keep it if hand-authored siblings would go down with it.
				delete effect.data.output[branch].attributes;
				if (Object.keys(effect.data.output[branch]).length === 0) delete effect.data.output[branch];
			}
		});
	}

	private autoGenerate(mediaId: string, mediaName: string): void {
		this.filesService.generateVersions(mediaId)
			.pipe(this.jobsService.andTrack({ mediaId, mediaName, destroyRef: this.destroyRef }))
			.subscribe();
	}

	protected startGenerate(): void {
		const media = this.currentBgMedia();
		const mediaId = media?.id;
		if (!mediaId) return;
		this.filesService.generateVersions(mediaId)
			.pipe(this.jobsService.andTrack({
				mediaId,
				mediaName: media?.name ?? '',
				mediaCategory: media?.category,
				destroyRef: this.destroyRef,
			}))
			.subscribe();
	}

	protected dismissGenerateStatus(): void {
		const mediaId = this.currentBgMedia()?.id;
		const job = mediaId ? this.jobsService.getJobForMedia(mediaId) : undefined;
		if (job) this.jobsService.dismissJob(job.jobId);
	}

	// ── Mask version generation ─────────────────────────────────────────────
	protected currentMaskMedia = computed(() => {
		const maskSrc = this.data()?.background?.source?.maskSrc;
		if (!maskSrc || typeof maskSrc !== 'string') return undefined;
		return this.filesService.findByBaseUrl(maskSrc);
	});

	protected canGenerateMask = computed(() => !!this.currentMaskMedia()?.isLocal);
	protected hasMaskVersions = computed(() => (this.currentMaskMedia()?.versionCount ?? 0) > 1);

	protected generateMaskStatus = computed<JobStatusResponse | null>(() => {
		const mediaId = this.currentMaskMedia()?.id;
		if (!mediaId) return null;
		const job = this.jobsService.getJobForMedia(mediaId);
		if (!job) return null;
		return {
			jobId: job.jobId, type: job.type, mediaId: job.mediaId,
			status: job.status, progress: job.progress,
			currentStep: job.currentStep, completedVersions: job.completedVersions,
			totalVersions: job.totalVersions, error: job.error,
		};
	});

	protected startGenerateMask(): void {
		const media = this.currentMaskMedia();
		const mediaId = media?.id;
		if (!mediaId) return;
		this.filesService.generateVersions(mediaId)
			.pipe(this.jobsService.andTrack({
				mediaId,
				mediaName: media?.name ?? '',
				mediaCategory: media?.category,
				destroyRef: this.destroyRef,
			}))
			.subscribe();
	}

	protected dismissGenerateMaskStatus(): void {
		const mediaId = this.currentMaskMedia()?.id;
		const job = mediaId ? this.jobsService.getJobForMedia(mediaId) : undefined;
		if (job) this.jobsService.dismissJob(job.jobId);
	}

	// ── Video Mask composite ────────────────────────────────────────────────
	private compositeMediaId = signal<string | null>(null);

	protected compositeStatus = computed<JobStatusResponse | null>(() => {
		const mediaId = this.compositeMediaId();
		if (!mediaId) return null;
		const job = this.jobsService.getJobForMedia(mediaId);
		if (!job) return null;
		return {
			jobId: job.jobId, type: job.type, mediaId: job.mediaId,
			status: job.status, progress: job.progress,
			currentStep: job.currentStep, completedVersions: job.completedVersions,
			totalVersions: job.totalVersions, error: job.error,
		};
	});

	/** Both video and mask must be local media (for now). */
	protected canComposite = computed(() => {
		const video = this.currentBgMedia();
		const mask = this.currentMaskMedia();
		return !!video?.isLocal && !!video.id && !!mask?.isLocal && !!mask.id;
	});

	/** Backend rejects with 409 unless both sources have versions beyond `original`. */
	protected compositeReady = computed(() => this.hasVersions() && this.hasMaskVersions());

	protected compositeTooltip = computed(() => {
		if (this.compositeReady()) return 'Create video_mask composite (vstack video + inverted mask)';
		const missingVideo = !this.hasVersions();
		const missingMask = !this.hasMaskVersions();
		if (missingVideo && missingMask) return 'Generate versions for both video and mask first';
		if (missingVideo) return 'Generate versions for the video first';
		return 'Generate versions for the mask first';
	});

	protected startComposite(): void {
		const videoId = this.currentBgMedia()?.id;
		const maskId = this.currentMaskMedia()?.id;
		if (!videoId || !maskId) return;
		this.fireComposite(videoId, maskId);
	}

	/**
	 * DEV-128: shared by the "Create Video Mask" button (`startComposite`) and
	 * the auto-fire paths in `setBgSrc`/`setMaskSrc` (when the side just set
	 * is already generated and the other side turns out to be too).
	 */
	private fireComposite(videoId: string, maskId: string): void {
		this.filesService.createVideoMask(videoId, maskId).subscribe({
			next: ({ jobId, newMediaId }) => {
				this.compositeMediaId.set(newMediaId);
				this.jobsService.trackJob({
					jobId, type: 'video-mask', mediaId: newMediaId,
					mediaName: 'video-mask composite',
					mediaCategory: 'video-mask',
					status: 'pending', progress: 0,
					currentStep: 'Queued', completedVersions: [], totalVersions: 0,
				});
			},
		});
	}

	protected dismissCompositeStatus(): void {
		const mediaId = this.compositeMediaId();
		const job = mediaId ? this.jobsService.getJobForMedia(mediaId) : undefined;
		if (job) this.jobsService.dismissJob(job.jobId);
		this.compositeMediaId.set(null);
	}
}
